import { json, redirect } from "@remix-run/node";
import type { ActionFunctionArgs, LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { Form, Link, useActionData, useNavigation } from "@remix-run/react";
import { getUser, sessionStorage } from "~/lib/session.server";
import { db } from "~/lib/db.server";
import bcrypt from "bcryptjs";
import { BloodhoundLogo } from "~/components/BloodhoundLogo";

export const meta: MetaFunction = () => [
  { title: "Create account — Bloodhound" },
];

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await getUser(request);
  if (user) throw redirect("/dashboard");
  return {};
}

export async function action({ request }: ActionFunctionArgs) {
  const form = await request.formData();
  const name = (form.get("name") as string)?.trim();
  const email = (form.get("email") as string)?.trim().toLowerCase();
  const password = form.get("password") as string;
  const confirm = form.get("confirm") as string;

  if (!name || !email || !password) {
    return json({ error: "All fields are required." }, { status: 400 });
  }
  if (password.length < 8) {
    return json({ error: "Password must be at least 8 characters." }, { status: 400 });
  }
  if (password !== confirm) {
    return json({ error: "Passwords don't match." }, { status: 400 });
  }

  const existing = await db.workspaceUser.findFirst({ where: { email } });
  if (existing) {
    return json({ error: "An account with that email already exists." }, { status: 409 });
  }

  const passwordHash = await bcrypt.hash(password, 12);

  // Check for referral cookie
  const cookieHeader = request.headers.get("Cookie") ?? "";
  const refMatch = cookieHeader.match(/(?:^|;\s*)__bhd_ref=([^;]+)/);
  const refCode = refMatch ? decodeURIComponent(refMatch[1]) : null;

  let referredBy: string | null = null;
  if (refCode) {
    const affiliate = await db.affiliate.findUnique({
      where: { code: refCode },
      select: { id: true, active: true },
    });
    if (affiliate?.active) referredBy = affiliate.id;
  }

  const workspace = await db.workspace.create({
    data: { name: `${name}'s workspace`, referredBy },
  });

  const trialEnd = new Date();
  trialEnd.setDate(trialEnd.getDate() + 14);

  await db.workspaceBilling.create({
    data: {
      workspaceId: workspace.id,
      trialEndsAt: trialEnd,
      currentPeriodEnd: trialEnd,
    },
  });

  const user = await db.workspaceUser.create({
    data: {
      workspaceId: workspace.id,
      email,
      name,
      passwordHash,
    },
  });

  const session = await sessionStorage.getSession(request.headers.get("Cookie"));
  session.set("workspaceUserId", user.id);

  return redirect("/dashboard", {
    headers: { "Set-Cookie": await sessionStorage.commitSession(session) },
  });
}

export default function Register() {
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const submitting = navigation.state === "submitting";

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#080808] relative overflow-hidden">
      <div className="radar-sweep" />
      <div className="radar-rings">
        <span /><span /><span /><span />
      </div>

      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse 600px 500px at 50% 55%, rgba(220,38,38,0.04) 0%, transparent 70%)",
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      />

      <div className="relative z-10 w-full max-w-sm px-6">
        <div className="flex flex-col items-center mb-10">
          <BloodhoundLogo size="lg" />
          <p className="mt-4 text-gray-400 text-sm text-center leading-relaxed">
            Monitor every Shopify store.<br />From one place.
          </p>
        </div>

        <div className="bg-white/[0.04] border border-white/[0.08] rounded-lg p-7 shadow-2xl shadow-black/40 backdrop-blur-md">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider text-center mb-5">
            Create your account
          </p>

          <Form method="post" className="space-y-3">
            {actionData?.error && (
              <p className="text-xs text-red-400 text-center bg-red-950/30 border border-red-800/30 rounded-lg px-3 py-2">
                {actionData.error}
              </p>
            )}
            <input
              name="name"
              type="text"
              placeholder="Full name"
              autoComplete="name"
              required
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-white/20 focus:ring-1 focus:ring-white/10 transition-colors"
            />
            <input
              name="email"
              type="email"
              placeholder="Email"
              autoComplete="email"
              required
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-white/20 focus:ring-1 focus:ring-white/10 transition-colors"
            />
            <input
              name="password"
              type="password"
              placeholder="Password (min 8 characters)"
              autoComplete="new-password"
              required
              minLength={8}
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-white/20 focus:ring-1 focus:ring-white/10 transition-colors"
            />
            <input
              name="confirm"
              type="password"
              placeholder="Confirm password"
              autoComplete="new-password"
              required
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-white/20 focus:ring-1 focus:ring-white/10 transition-colors"
            />
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#dc2626] text-white font-semibold text-sm px-4 py-3.5 rounded-xl hover:bg-[#b91c1c] active:bg-[#991b1b] transition-colors duration-150 disabled:opacity-60 focus-visible:ring-2 focus-visible:ring-red-500/50 focus-visible:outline-none"
            >
              {submitting ? "Creating account…" : "Create account"}
            </button>
          </Form>

          <p className="mt-4 text-center text-xs text-gray-500">
            Already have an account?{" "}
            <Link to="/login" className="text-gray-400 hover:text-gray-300 underline underline-offset-2">
              Sign in
            </Link>
          </p>

          <p className="mt-5 text-center text-xs text-gray-500 leading-relaxed">
            By creating an account you agree to our{" "}
            <a href="/terms" className="text-gray-400 hover:text-gray-300 underline underline-offset-2">
              Terms
            </a>{" "}
            and{" "}
            <a href="/privacy" className="text-gray-400 hover:text-gray-300 underline underline-offset-2">
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
